import { supabase } from '@/integrations/supabase/client';

export interface PickedFolder {
  id: string;
  name: string;
}

// google.picker's types aren't worth adding a dependency for — the widget
// is loaded from Google's CDN at runtime (see loadPickerApi below), not
// bundled, so there's no local type package that would actually match what
// gets loaded. Typed loosely as `any` at the boundary only.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GooglePickerNamespace = any;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gapi?: any;
    google?: { picker: GooglePickerNamespace };
  }
}

let pickerApiLoadPromise: Promise<void> | null = null;

/**
 * Loads Google's `api.js` and its `picker` module once per page load.
 * Subsequent calls reuse the same in-flight/completed promise instead of
 * re-injecting the script tag.
 */
function loadPickerApi(): Promise<void> {
  if (pickerApiLoadPromise) return pickerApiLoadPromise;

  pickerApiLoadPromise = new Promise((resolve, reject) => {
    if (window.gapi?.load) {
      window.gapi.load('picker', { callback: resolve, onerror: reject });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.onload = () => {
      window.gapi!.load('picker', { callback: resolve, onerror: reject });
    };
    script.onerror = () => reject(new Error('Failed to load Google API script'));
    document.head.appendChild(script);
  });

  return pickerApiLoadPromise;
}

/**
 * Opens the Google Picker, scoped to folder selection, for the given
 * connected google_drive integration. Fetches a short-lived access token
 * from google-drive-picker-token (the stored refresh token never reaches
 * the browser) and lets the admin pick one or more folders.
 *
 * Resolves to the picked folders, or null if the picker was cancelled.
 * Throws if the access-token fetch fails or the Picker API can't load.
 */
export async function openGoogleDriveFolderPicker(integrationId: string): Promise<PickedFolder[] | null> {
  const apiKey = import.meta.env.VITE_GOOGLE_PICKER_API_KEY;
  if (!apiKey) {
    throw new Error('Google Picker is not configured (VITE_GOOGLE_PICKER_API_KEY is unset).');
  }

  const { data, error } = await supabase.functions.invoke('google-drive-picker-token', {
    body: { integrationId },
  });
  if (error) throw error;
  const accessToken = (data as { accessToken?: string } | null)?.accessToken;
  if (!accessToken) throw new Error('Could not obtain a Google Drive access token.');

  await loadPickerApi();
  const google = window.google;
  if (!google?.picker) throw new Error('Google Picker failed to load.');

  return new Promise((resolve, reject) => {
    try {
      // ViewId.FOLDERS + setSelectFolderEnabled(true) alone only controls
      // *navigation* (browse into folders) and which folder gets selected
      // when the user clicks "Select" while inside it — it does NOT stop
      // the view from also listing individual files inside each folder as
      // separately clickable/selectable items. Without setMimeTypes scoping
      // the view's own item list to folders, a user can select a regular
      // file (confirmed in testing: a .txt file got picked instead of its
      // parent folder). Restricting to the folder mimeType is what actually
      // makes files unselectable in this view.
      const view = new google.picker.DocsView(google.picker.ViewId.FOLDERS)
        .setSelectFolderEnabled(true)
        .setIncludeFolders(true)
        .setMimeTypes('application/vnd.google-apps.folder');

      const picker = new google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(accessToken)
        .setDeveloperKey(apiKey)
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .setCallback((pickerData: { action: string; docs?: { id: string; name: string; mimeType?: string }[] }) => {
          if (pickerData.action === google.picker.Action.PICKED) {
            // Defense in depth against the view-config quirk above (or any
            // future Picker behavior change): never persist a non-folder
            // pick as if it were a folder.
            const picked = pickerData.docs ?? [];
            const folders = picked
              .filter((doc) => doc.mimeType === 'application/vnd.google-apps.folder')
              .map((doc) => ({ id: doc.id, name: doc.name }));
            if (folders.length === 0 && picked.length > 0) {
              reject(new Error('Only folders can be selected — please pick a folder, not a file.'));
              return;
            }
            resolve(folders.length > 0 ? folders : null);
          } else if (pickerData.action === google.picker.Action.CANCEL) {
            resolve(null);
          }
        })
        .build();
      picker.setVisible(true);
    } catch (err) {
      reject(err);
    }
  });
}

/** Persists the picked folders onto the integration's config (server-side merge). */
export async function saveGoogleDriveFolders(integrationId: string, folders: PickedFolder[]): Promise<void> {
  const { error } = await supabase.functions.invoke('google-drive-set-folders', {
    body: { integrationId, folders },
  });
  if (error) throw error;
}
